import React, {
    FC,
    Dispatch,
    SetStateAction,
    useState,
    useEffect,
} from "react";
import { MDBModal, MDBModalDialog, MDBModalContent } from "mdb-react-ui-kit";
import { useRouter } from "next/router";
import { useDisableBodyScroll } from "@/utils/hooks/useDisableBodyScroll";
import { useIsClient } from "@/utils/hooks/useIsClient";
import HeroSearchBar from "../home/homeHead/HeroSearchBar";
import styles from "../../styles/common/SearchBarModal.module.scss";

interface SearchBarModalProps {
    show: boolean;
    setShow: Dispatch<SetStateAction<boolean>>;
}

const SearchBarModal: FC<SearchBarModalProps> = ({ show, setShow }) => {
    const isClient = useIsClient();
    const router = useRouter();
    useDisableBodyScroll(show);
    const [isSearchFocused, setIsSearchFocused] = useState(false);

    // Chiudi la modale quando si naviga alla pagina tours-destinations
    useEffect(() => {
        const handleRouteChange = (url: string) => {
            if (url.startsWith("/tours-destinations")) {
                setShow(false);
                setIsSearchFocused(false);
            }
        };

        router.events.on("routeChangeStart", handleRouteChange);

        return () => {
            router.events.off("routeChangeStart", handleRouteChange);
        };
    }, [router, setShow]);

    // Focalizza automaticamente la searchbar quando il modal si apre
    useEffect(() => {
        if (show && isClient) {
            // Aspetta che il modal sia completamente renderizzato
            const timer = setTimeout(() => {
                const searchInput =
                    document.getElementById("hero-search-input");
                if (searchInput) {
                    searchInput.focus();
                    setIsSearchFocused(true);
                }
            }, 100);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [show, isClient]);

    const handleClose = (e?: React.MouseEvent | React.KeyboardEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setShow(false);
        setIsSearchFocused(false);
    };

    if (!isClient) {
        return null;
    }

    return (
        <MDBModal
            show={show}
            setShow={(value: boolean | ((prev: boolean) => boolean)) => {
                if (typeof value === "function") {
                    const newValue = value(show);
                    if (!newValue) {
                        handleClose();
                    }
                } else if (!value) {
                    handleClose();
                }
            }}
            tabIndex="-1"
            staticBackdrop={false}
            backdrop
            keyboard={false}
        >
            <MDBModalDialog
                size="fullscreen-xxl-down"
                className={styles.modalDialog}
                onClick={e => {
                    // Se si clicca sul dialog stesso (fuori dalla searchbar), chiudi il modal
                    if (e.target === e.currentTarget) {
                        handleClose();
                    }
                }}
                shouldCloseOnOverlayClick
            >
                <MDBModalContent
                    className={styles.modalContent}
                    onClick={e => {
                        // Se si clicca sul content stesso (fuori dalla searchbar), chiudi il modal
                        if (e.target === e.currentTarget) {
                            handleClose();
                        }
                    }}
                >
                    {isSearchFocused && (
                        <div
                            className={styles.searchOverlay}
                            onClick={handleClose}
                        />
                    )}
                    <div
                        className={`${styles.searchBarContainer} ${styles.searchBarModalContainer}`}
                        onClick={e => e.stopPropagation()}
                    >
                        <HeroSearchBar
                            onFocusChange={setIsSearchFocused}
                            disableActiveAnimation
                        />
                    </div>
                </MDBModalContent>
            </MDBModalDialog>
        </MDBModal>
    );
};

export default SearchBarModal;
